import { IProjectMetadata, IR23Report, IR23ReportMetadata } from '@/interfaces/report';
import { UnitSystem } from '@/interfaces/units';
import { BaseScenarioType, IBaseScenario } from '@/lib/types/IBaseScenario';
import {
  IAgencyLineItemImperial,
  ILccaActivityImperial,
  ILccaAlternativeImperial,
  ILccaCalculatedImperial,
} from '@/modules/lcca/interfaces/lccaCalculated';
import { ImperialUnits } from '@/lib/unitConversion/unitConversionFactors';
import { DistributionTypes, IDistribution } from '@/lib/monteCarlo/interfaces';

export const getScenarioMetadataFromR23Report = function (meta: IProjectMetadata,
                                                          report: IR23Report,
                                                          scenarioType: BaseScenarioType): IBaseScenario {
  const sm: IBaseScenario = {
    id: null,
    user: null,
    project: meta.id,
    scenarioType,
    name: report.projectName || 'Unnamed',
    created: meta.created,
    modified: meta.modified,
    visible: meta.visible,
    isComputedFromLegacy: true,
    displayUnits: UnitSystem.Imperial,

  };

  return sm;
};


export const getLccaFromR23Report = function (meta: IR23ReportMetadata, report: IR23Report, projectId: number): ILccaCalculatedImperial {
  const ds: ILccaCalculatedImperial = {
    id: null,
    user: null,
    scenarioType: BaseScenarioType.lcca,
    name: `${report.projectName} Converted` || 'Default LCCA Project Name',
    created: meta.created,
    isComputedFromLegacy: true,
    project: projectId,
    modified: new Date(Date.now()).toISOString(),
    unitSystem: UnitSystem.Imperial,
    displayUnits: UnitSystem.Imperial,
    visible: true,

    // Scenario Information
    analysisDirection: report.analysisDirection || '', // Freeform, mostly unused
    analyzedBy: report.analyzedBy ? report.analyzedBy : null,
    projectExtent_begin: report.projectExtent_begin ? report.projectExtent_begin.toString() : null, // Basically unused
    projectExtent_end: report.projectExtent_end ? report.projectExtent_end.toString() : null, // ditto
    comments: report.comments || null,

    maxQueueLength: Number(report.maxQueueLength) ? Number(report.maxQueueLength) : 35,
    // probabilistic: report.probabilistic ? report.probabilistic : [],

    state: report.stateValue ? report.stateValue : 'US_WA',

    // Analysis Options
    analysisPeriodLength_yrs: report.analysisPeriod ? Number(report.analysisPeriod) : null, // analysisPeriod
    analysisPeriodStart_yr: report.beginningAnalysisPeriod ? Number(report.beginningAnalysisPeriod) : null, // beginningAnalysisPeriod -> sometimes used improperly in legacy
    userCostComputationMethod: 'Calculated',
    userCostsInAnalysis: true,

    discountRate: 0.01,

    // Traffic Data
    trafficYear: report.trafficYear ? Number(report.trafficYear) : null,

    annualGrowthTraffic: report.annualGrowthTraffic ? Number(report.annualGrowthTraffic) : 0,
    singleTruckAadtPct: report.singleTruckAADT ? Number(report.singleTruckAADT) : null, // singleTruckAADT
    combinationTruckAadtPct: report.combinationTruckAADT ? Number(report.combinationTruckAADT) : null, // combinationTruckAADT
    numLanesNormal: report.lanesNormalOpt ? Number(report.lanesNormalOpt) : null,

    aadtAnalysisPeriod: report.aadtAnalysisPeriod ? Number(report.aadtAnalysisPeriod) : null,
    freeFlowCapacity: report.freeFlowCapacity ? Number(report.freeFlowCapacity) : null, // vphpl
    speedLimitNormal_mph: report.speedLimitNormalOpt ? Number(report.speedLimitNormalOpt) : null,
    maxQueueLength_mi: report.maxQueueLength ? Number(report.maxQueueLength) : 15,
    noShowPct: report.noShowPercent ? Number(report.noShowPercent) : null,
    detourPct: report.detourPercent ? Number(report.detourPercent) : null,
    additionalDetourTime_min: report.additionalDetourTime ? Number(report.additionalDetourTime) : null,

    // maxQueueLength: report.maxQueueLength ? Number(report.maxQueueLength) : 15,

    // Traffic Hourly Distribution
    trafficHourlyDistribution: report.trafficHourlyDistribution ? report.trafficHourlyDistribution.map((x) => Number(x)) : [],
    patternType: report.patternType ? report.patternType : null,

    probabilistic: (function () {
      const probVariables: IDistribution[] = [];
      if (report.probabilistic) {
        Object.entries(report.probabilistic).forEach(([varName, params]) => {
          probVariables.push(<IDistribution>{
            id: null,
            name: varName,
            prob: params.Prob || DistributionTypes.deterministic,
            min: params.Min || 0,
            max: params.Max || 0,
            mean: params.Mean || 0,
            mode: params.Mode || 0,
            std: params.Std || 0,
          });
        });
      }
      return probVariables;
    }()),

    passengerCarsTime: report.passengerCarsTime ? Number(report.passengerCarsTime) : 11.58,
    combinationTrucksTime: report.combinationTrucksTime ? Number(report.combinationTrucksTime) : 22.31,
    singleUnitTrucksTime: report.singleUnitTrucksTime ? Number(report.singleUnitTrucksTime) : 18.54,

    alternatives: report.alternatives
      ? report.alternatives.map((alternative, newAltIndex) => {
        const pxAlternative: ILccaAlternativeImperial = {

          id: null,
          unitSystem: UnitSystem.Imperial,
          name: `${alternative.name} Converted`,
          index: newAltIndex,
          activities: alternative.activities
            ? alternative.activities.map((activity, newActIndex) => {
              // console.log("Activity", newActIndex, activity.index, newAltIndex);

              const pxActivity: ILccaActivityImperial = {

                unitSystem: UnitSystem.Imperial,

                id: null,
                name: activity.name,
                description: activity.desc || '',
                index: newActIndex,
                alternative: newAltIndex, // activity.altIndex,
                startYear: activity.startYear,
                serviceLife_yrs: activity.serviceLife,
                structuralLife_yrs: activity.structuralLife,
                agencyConstructionCost_thousands: Number(activity.agencyConstructionCost),
                agencyMaintenanceCost_thousands: Number(activity.agencyMaintenanceCost),

                userCostEnabled: Boolean(activity.noUserCost),
                workzoneLength_mi: Number(activity.workzoneLength),
                workzoneCapacity: Number(activity.workzoneCapacity),
                workzoneNumLanes: Number(activity.noLanesWorkzone),
                workzoneSpeedLimit_mph: Number(activity.workzoneSpeedLimit),
                workzoneDuration_days: Number(activity.workzoneDuration),

                userWorkzoneCost_thousands: Number(activity.userWorkzoneCost) || 0,

                laneClosePeriods: activity.laneClosePeriods || '',
                nightShift: activity.nightShift,


                lineItems: activity.lineItems
                  ? activity.lineItems.map((L) => {
                    const pxL: IAgencyLineItemImperial = {
                      id: null,
                      name: L.name,
                      unitSystem: UnitSystem.Imperial,
                      units: (function () {
                        // Cast into known units
                        if ((<any>ImperialUnits)[L.units]) {
                          return (<any>ImperialUnits)[L.units];
                        } 
                          return ImperialUnits.UNITS;
                      }()),
                      unitDescr: null,
                      unitCost: Number(L.price),
                      quantity: Number(L.quantity),
                    };

                    // Save old unit type as 'other' if unit type not found
                    if (pxL.units === ImperialUnits.UNITS) {
                      pxL.unitDescr = L.units;
                    }

                    return pxL;
                  }) : [],
              };
              return pxActivity;
            }) : [],
        };

        return pxAlternative;
      }) : [],

  };

  // ds.alternatives.forEach(A => console.log(A.index, A.name));

  return ds;
};
